/** Wholesaler Review queue (v1 scrapers.js wholesalerReviewPanel + approve/
 * rejectWholesaler). Deals submitted by wholesalers land in the WHOLESALER_QUEUE
 * org-settings blob (whitelisted in PHASE22). Approve → create a Pre-Check lead
 * (the live leads door) and drop the item from the queue; Reject → just drop it.
 * Both queue writes are whole-array replaces through the settings door. */
import type { RecOp } from "../../lib/outbox";
import { moneyExact } from "../../lib/money";
import { withLedgerBase } from "../settings/api";

export interface QueueItem {
  id: string;
  a: string; // address
  src: string; // where it came from (wholesaler / Facebook / …)
  price: string; // ask (display)
  arv: string; // ARV (display)
  seller: string;
}

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));

/** Format a raw ask/ARV (number or "$172,000" or "172000") to a "$…" display. */
function money(v: unknown): string {
  if (v == null || v === "") return "";
  const s = String(v).trim();
  if (s.startsWith("$")) return s;
  const n = Number(s.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) && n !== 0 ? moneyExact(n) : s;
}

let _qid = 0;
const queueId = (): string => "wq" + Date.now().toString(36) + (_qid++).toString(36);

export function narrowQueueItem(raw: unknown): QueueItem {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    id: str(o["id"]) || queueId(),
    a: str(o["a"] ?? o["addr"] ?? o["address"]),
    src: str(o["src"] ?? o["source"]),
    price: money(o["price"] ?? o["ask"]),
    arv: money(o["arv"]),
    seller: str(o["seller"] ?? o["seller_name"] ?? o["who"]),
  };
}

export function allQueue(orgSettings: Record<string, unknown>): QueueItem[] {
  const raw = orgSettings["WHOLESALER_QUEUE"];
  // Keep only items with an address (v1 shows address-bearing submissions).
  return (Array.isArray(raw) ? raw.map(narrowQueueItem) : []).filter((q) => q.a.trim() !== "");
}

/** Whole-array replace of the queue through the settings door. */
export function opPutQueue(all: QueueItem[]): RecOp {
  return withLedgerBase({ s: "settings", op: "put", k: "WHOLESALER_QUEUE", d: all as unknown as Record<string, unknown>[] });
}

/** v1 approveWholesaler: the new lead row for the leads door (opAddLead). */
export function queueItemToLeadRow(item: QueueItem, nowMs: number): Record<string, unknown> {
  const d = new Date(nowMs);
  return {
    a: item.a,
    b: ["Wholesaler", "New"],
    who: item.seller,
    price: item.price || "$0",
    arv: item.arv || "—",
    cty: "—",
    beds: 0,
    baths: 0,
    sqft: "—",
    rehab: "—",
    roi: "—",
    stage: "Pre-Check",
    qual: "Pre-Check",
    source: "Wholesaler",
    jobTag: "Wholesaler Review",
    marketStatus: "off",
    _ts: nowMs,
    dt: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
  };
}
